/**
 * Tipos e chamadas do catálogo de produtos.
 *
 * O contrato espelha `api/internal/modulos/produtos/dominio.go`. Dois detalhes
 * carregam significado e valem ser lidos antes de mexer na tela:
 *
 * 1. `custos` e `celula.custo_centavos` são OPCIONAIS porque o servidor os
 *    omite de quem não tem `cost.read`. A tela não decide isso — ela apenas
 *    não desenha o que não veio (CONSTITUICAO.md §4.1). `ve_custo` diz se a
 *    resposta traz custo, e serve para decidir se a COLUNA existe: uma lista
 *    vazia não tem primeiro item de onde inferir.
 * 2. `desempenho.venda.fonte` é `aguarda_venda` enquanto o módulo de venda não
 *    existir. A tela mostra o texto de espera, e não o zero — zero ali
 *    pareceria medição.
 */
import { api } from './api'
import type { Centavos } from './dinheiro'
// A conversão de ponto-base para "63%" já existe e é a mesma conta do painel.
// Duas implementações da mesma formatação viram dois números diferentes na
// primeira vez que uma delas mudar de arredondamento.
export { formatarPontos } from './painel'

/** Ponto-base: 10000 = 100 %. */
export type Pontos = number

export type Fonte = 'real' | 'aguarda_venda'

export type Categoria = 'roupas' | 'tenis' | 'acessorios' | 'equipamentos'

/**
 * Situação derivada do SALDO, não de uma coluna do banco.
 *
 * A spec 03 lista uma quarta, "Rascunho", que não existe: `produto.ativo` é
 * booleano no banco (ver docs/de-para.md). O filtro não a oferece porque
 * escolhê-la devolveria lista sempre vazia sem nenhuma explicação.
 */
export type Situacao = 'ativo' | 'grade_furada' | 'esgotado'

export type Filial = {
  codigo: string
  nome: string
  principal: boolean
}

export type Cor = {
  nome: string
  hex?: string
}

/** Uma coluna da tira: um tamanho com o saldo somado das cores. */
export type PosicaoGrade = {
  tamanho: string
  saldo: number
  estoque_minimo: number
  /** Quantas cores existem naquele tamanho. */
  cores: number
}

export type Custos = {
  custo_centavos: Centavos
  /** Ausente quando o preço é zero: não existe margem sobre preço nenhum. */
  margem_pontos_base?: Pontos
}

export type LinhaProduto = {
  codigo: string
  nome: string
  categoria: Categoria
  categoria_rotulo: string
  marca?: string
  preco_centavos: Centavos
  skus: number
  cores: Cor[]
  /** Segue a ordem da grade da loja, não a alfabética. */
  tira_grade: PosicaoGrade[]
  pecas: number
  tamanhos_zerados: number
  situacao: Situacao
  situacao_rotulo: string
  /**
   * `produto.ativo` — o produto sai da vitrine e continua no cadastro.
   *
   * É diferente de `situacao`, que é derivada do SALDO: um produto inativo
   * pode estar cheio de peça no depósito.
   */
  ativo: boolean
  /** Quantas fotos o produto tem. A capa é a de ordem 0. */
  fotos: number
  /** Ausente para quem não tem `cost.read`. */
  custos?: Custos
}

export type AbaCategoria = {
  chave: Categoria
  rotulo: string
  produtos: number
}

export type ResumoCatalogo = {
  ativos: number
  skus: number
  grades_furadas: number
  esgotados: number
  valor_estoque_custo_centavos?: Centavos
  margem_media_pontos_base?: Pontos
}

export type Catalogo = {
  filial: Filial
  filiais: Filial[]
  resumo: ResumoCatalogo
  categorias: AbaCategoria[]
  produtos: LinhaProduto[]
  total: number
  ve_custo: boolean
}

export type CelulaFicha = {
  /** `false` é "esta cor não vem neste tamanho" — diferente de `saldo === 0`. */
  existe: boolean
  tamanho: string
  codigo_barras?: string
  saldo: number
  estoque_minimo: number
  preco_centavos: Centavos
  custo_centavos?: Centavos
}

export type LinhaCorFicha = {
  cor: string
  hex?: string
  /** Uma célula por coluna de `tamanhos`, sempre. */
  celulas: CelulaFicha[]
  total: number
}

/**
 * Uma linha da LISTA de SKUs da ficha.
 *
 * É outra pergunta que a matriz: a matriz responde "onde está faltando", a
 * lista responde "qual é o código desta peça". O EAN-13 não cabe numa célula
 * de 46 px, e é ele que a pessoa lê para bipar no PDV, conferir uma nota ou
 * imprimir etiqueta.
 *
 * `ativo: false` é a variação que saiu da grade e continua no banco — o
 * histórico de estoque aponta para ela e a etiqueta dela ainda circula.
 */
export type VariacaoFicha = {
  cor: string
  hex?: string
  tamanho: string
  /** O código CANÔNICO — o EAN-13 interno que o sistema gerou e que a
   * etiqueta dele imprime. É a identidade da variação em toda a API. */
  codigo_barras: string
  saldo: number
  estoque_minimo: number
  preco_centavos: Centavos
  /** Ausente para quem não tem `cost.read`. */
  custo_centavos?: Centavos
  ativo: boolean
  /**
   * Códigos do FABRICANTE que também bipam nesta peça (migração 0025).
   *
   * Existe para a loja que já tem estoque etiquetado: a peça responde pelo
   * EAN impresso nela sem ninguém reetiquetar nada. Vazio na maioria dos
   * casos — a maioria das lojas imprime a própria etiqueta.
   */
  codigos_alternativos?: string[]
}

export type DesempenhoGrade = {
  fonte: Fonte
  tamanhos: number
  tamanhos_zerados: number
  posicoes: number
  posicoes_com_saldo: number
  cobertura_pontos_base: Pontos
}

export type DesempenhoVenda = {
  fonte: Fonte
  pecas_30d: number
  cobertura_dias: number
  espera?: string
}

export type Desempenho = {
  grade: DesempenhoGrade
  venda: DesempenhoVenda
}

/** Uma foto do produto, sem o binário — a imagem vem por URL própria. */
export type Foto = {
  ordem: number
  nome_arquivo: string
  tipo_conteudo: string
  /** TAMANHO em bytes, não o conteúdo. */
  bytes: number
  /** `ordem === 0`. Vem calculado para a tela não repetir a regra. */
  capa: boolean
}

export type Ficha = {
  codigo: string
  nome: string
  categoria: Categoria
  categoria_rotulo: string
  marca?: string
  preco_centavos: Centavos
  situacao: Situacao
  situacao_rotulo: string
  ativo: boolean
  filial: Filial
  filiais: Filial[]
  fotos: Foto[]
  tamanhos: string[]
  cores: LinhaCorFicha[]
  tira_grade: PosicaoGrade[]
  /** A lista de SKUs, com o código de barras. Traz as desativadas, marcadas. */
  variacoes: VariacaoFicha[]
  pecas: number
  /** Conta só as ATIVAS: é a grade vendável. */
  skus: number
  /** Quantas saíram da grade e continuam no banco. */
  skus_inativos: number
  custos?: Custos
  desempenho: Desempenho
  aviso_grade?: string
  ve_custo: boolean
}

export type VariacaoCriada = {
  cor: string
  hex?: string
  tamanho: string
  codigo_barras: string
}

export type ResultadoCadastro = {
  codigo: string
  nome: string
  categoria: Categoria
  categoria_rotulo: string
  preco_centavos: Centavos
  skus: number
  cores: number
  tamanhos: number
  estoque_minimo: number
  variacoes: VariacaoCriada[]
  custos?: Custos
  ve_custo: boolean
}

/**
 * O corpo de POST /produtos.
 *
 * Preço e custo viajam como TEXTO, e não como número: quem digita escreve
 * "189,90", e a conversão para centavos acontece no servidor, na borda
 * (CONSTITUICAO.md §1). Mandar `189.90` deixaria o serializador do navegador
 * decidir sozinho quantas casas o número tem.
 */
export type PedidoCadastro = {
  nome: string
  codigo?: string
  categoria: Categoria
  marca?: string
  preco: string
  custo?: string
  estoque_minimo?: number
  cores: Cor[]
  tamanhos: string[]
}

/**
 * As quatro categorias do banco (CHECK da migração 0003).
 *
 * O protótipo traz "Suplementos" como quarta aba; o banco traz
 * "Equipamentos", e o banco manda (docs/de-para.md). A listagem recebe as abas
 * DO SERVIDOR com a contagem; esta lista existe para o formulário de cadastro,
 * que precisa das opções antes de qualquer resposta.
 */
export const CATEGORIAS: { chave: Categoria; rotulo: string }[] = [
  { chave: 'roupas', rotulo: 'Roupas' },
  { chave: 'tenis', rotulo: 'Tênis' },
  { chave: 'acessorios', rotulo: 'Acessórios' },
  { chave: 'equipamentos', rotulo: 'Equipamentos' },
]

export const FILTROS_SITUACAO: { chave: Situacao | ''; rotulo: string }[] = [
  { chave: '', rotulo: 'Todas' },
  { chave: 'ativo', rotulo: 'Ativos' },
  { chave: 'grade_furada', rotulo: 'Grade furada' },
  { chave: 'esgotado', rotulo: 'Esgotados' },
]

/**
 * O corpo de PUT /produtos/{codigo}.
 *
 * TODO campo é opcional, e ausente significa "não mexa". É o que permite ao
 * menu ⋮ da listagem inativar um produto com `{ ativo: false }` em vez de
 * reenviar a grade inteira num pedido que não é sobre a grade.
 *
 * `codigo` não está aqui: ele é identidade de negócio, está impresso na
 * etiqueta de cada peça e compõe a URL da ficha (CONSTITUICAO.md §2).
 *
 * `cores` e `tamanhos` vão JUNTOS ou não vão — o servidor recusa meia grade,
 * porque metade de uma redefinição desativaria variação que ninguém pediu para
 * desativar.
 */
export type PedidoEdicao = {
  nome?: string
  categoria?: Categoria
  marca?: string
  preco?: string
  /** Vazio/ausente PRESERVA o custo de cada variação. */
  custo?: string
  estoque_minimo?: number
  cores?: Cor[]
  tamanhos?: string[]
  ativo?: boolean
}

export type VariacaoResumo = {
  cor: string
  tamanho: string
}

export type VariacaoDesativada = {
  cor: string
  tamanho: string
  saldo: number
  /** Tinha saldo ou movimento: foi desativada, não removida. */
  com_historico: boolean
}

export type ResultadoEdicao = {
  codigo: string
  nome: string
  categoria: Categoria
  categoria_rotulo: string
  marca?: string
  preco_centavos: Centavos
  ativo: boolean
  skus: number
  cores: number
  tamanhos: number
  variacoes_criadas: VariacaoCriada[]
  variacoes_reativadas: VariacaoResumo[]
  variacoes_desativadas: VariacaoDesativada[]
  precos_mantidos: number
  /** Frases em pt-BR prontas: quem sabe o que a grade sofreu é o servidor. */
  avisos: string[]
  custos?: Custos
  ve_custo: boolean
}

export type ListaDeFotos = {
  codigo: string
  fotos: Foto[]
  total: number
  limite: number
}

/**
 * Um SKU na busca FLAT — a de GET /produtos/skus, que cruza TODOS os produtos
 * da loja de uma vez, ao contrário de `VariacaoFicha`, que é por produto.
 *
 * É o que a tela de Etiquetas precisa: selecionar peças de produtos
 * diferentes numa impressão só, sem abrir uma ficha por vez.
 *
 * `saldo` soma TODAS as filiais, porque etiqueta é por PEÇA FÍSICA e não por
 * unidade — quem imprime está com a peça na mão.
 *
 * `custo_centavos` é opcional pela mesma razão do resto do módulo: o servidor
 * o omite de quem não tem `cost.read` (CONSTITUICAO.md §4.1). A etiqueta
 * nunca o imprime — ela vai para a mão do cliente —, mas o campo existe no
 * contrato porque quem responde é o mesmo serviço do catálogo.
 */
export type LinhaSKU = {
  codigo_barras: string
  /** `{código do produto}-{COR}-{TAMANHO}` — o que a etiqueta escreve. */
  codigo_legivel: string
  produto: string
  codigo_produto: string
  categoria: Categoria
  categoria_rotulo: string
  cor: string
  cor_hex?: string
  tamanho: string
  preco_centavos: Centavos
  custo_centavos?: Centavos
  saldo: number
}

export type ListaDeSKUs = {
  skus: LinhaSKU[]
  total: number
  ve_custo: boolean
}

export function carregarSKUs(
  parametros: { busca?: string; categoria?: Categoria | '' },
  sinal?: AbortSignal,
): Promise<ListaDeSKUs> {
  const params = new URLSearchParams()
  if (parametros.busca) params.set('busca', parametros.busca)
  if (parametros.categoria) params.set('categoria', parametros.categoria)
  return api.get<ListaDeSKUs>(`/produtos/skus?${params}`, sinal)
}

export function carregarCatalogo(
  parametros: {
    busca?: string
    categoria?: Categoria | ''
    situacao?: Situacao | ''
    filial?: string
    inativos?: boolean
  },
  sinal?: AbortSignal,
): Promise<Catalogo> {
  const params = new URLSearchParams()
  if (parametros.busca) params.set('busca', parametros.busca)
  if (parametros.categoria) params.set('categoria', parametros.categoria)
  if (parametros.situacao) params.set('situacao', parametros.situacao)
  if (parametros.filial) params.set('filial', parametros.filial)
  if (parametros.inativos) params.set('inativos', '1')
  return api.get<Catalogo>(`/produtos?${params}`, sinal)
}

export function carregarFicha(
  codigo: string,
  filial?: string,
  sinal?: AbortSignal,
): Promise<Ficha> {
  const params = new URLSearchParams()
  if (filial) params.set('filial', filial)
  return api.get<Ficha>(`/produtos/${encodeURIComponent(codigo)}?${params}`, sinal)
}

export function cadastrarProduto(pedido: PedidoCadastro): Promise<ResultadoCadastro> {
  return api.post<ResultadoCadastro>('/produtos', pedido)
}

export function editarProduto(codigo: string, pedido: PedidoEdicao): Promise<ResultadoEdicao> {
  return api.put<ResultadoEdicao>(`/produtos/${encodeURIComponent(codigo)}`, pedido)
}

/**
 * Liga e desliga o produto — o item Ativar/Inativar do menu ⋮.
 *
 * Manda SÓ o `ativo`. Reenviar o resto seria mandar de volta o que a linha da
 * listagem tem, que é um recorte do produto: a grade não vai ali, e um PUT com
 * a grade ausente é justamente o que o contrato define como "não mexa".
 */
export function alternarAtivo(codigo: string, ativo: boolean): Promise<ResultadoEdicao> {
  return editarProduto(codigo, { ativo })
}

// -------------------------------------------------------------------- fotos

/**
 * A URL da imagem de uma posição.
 *
 * É `<img src>`, e não `fetch`: o cookie de sessão viaja igual e o navegador
 * cacheia por conta própria (o servidor manda `Cache-Control: private`).
 * Carregar por fetch e virar `blob:` custaria memória e perderia o cache.
 */
export function urlDaFoto(codigo: string, ordem: number): string {
  return `/api/v1/produtos/${encodeURIComponent(codigo)}/fotos/${ordem}`
}

/** A capa é a ordem 0. `null` quando o produto não tem foto. */
export function urlDaCapa(codigo: string, fotos: number): string | null {
  return fotos > 0 ? urlDaFoto(codigo, 0) : null
}

/**
 * Envia uma foto. O corpo é o BINÁRIO CRU, como o XML da NF-e.
 *
 * Sem `FormData`: é sempre um arquivo só, e envolvê-lo em multipart obrigaria
 * o servidor a carregar um parser inteiro para extrair a única parte que
 * interessa.
 */
export function enviarFoto(codigo: string, arquivo: File): Promise<ListaDeFotos> {
  const nome = encodeURIComponent(arquivo.name || 'foto')
  return api.postArquivo<ListaDeFotos>(
    `/produtos/${encodeURIComponent(codigo)}/fotos?nome=${nome}`,
    arquivo,
    arquivo.type,
  )
}

export function removerFoto(codigo: string, ordem: number): Promise<ListaDeFotos> {
  return api.delete<ListaDeFotos>(`/produtos/${encodeURIComponent(codigo)}/fotos/${ordem}`)
}

export function definirCapa(codigo: string, ordem: number): Promise<ListaDeFotos> {
  return api.put<ListaDeFotos>(`/produtos/${encodeURIComponent(codigo)}/fotos/${ordem}/capa`)
}

// ---------------------------------------------- código de barras do fabricante

/**
 * O que toda escrita de código alternativo devolve: a lista COMPLETA da
 * variação — mesmo raciocínio de `ListaDeFotos`, para a tela não refazer a
 * conta do que sobrou depois de remover.
 */
export type SaidaCodigosAlternativos = {
  produto: string
  sku: string
  codigos: string[]
  limite: number
}

/** Teto de códigos de fabricante por variação — espelha `MaxCodigosPorSKU` no Go. */
export const MAX_CODIGOS_ALTERNATIVOS = 3

/**
 * Pendura na variação um código que já está impresso na etiqueta do
 * fabricante. Depois disso a peça responde por DOIS números: o canônico, que
 * a etiqueta do sistema imprime, e este.
 *
 * `sku` é o código de barras CANÔNICO da variação — é o que identifica QUAL
 * peça está ganhando o apelido, e não a URL: código de barras em segmento de
 * caminho depende de escape que some no primeiro proxy mal configurado.
 */
export function adicionarCodigoAlternativo(
  produtoCodigo: string,
  sku: string,
  codigo: string,
): Promise<SaidaCodigosAlternativos> {
  return api.post<SaidaCodigosAlternativos>(
    `/produtos/${encodeURIComponent(produtoCodigo)}/codigos-alternativos`,
    { sku, codigo },
  )
}

/**
 * Tira o apelido de leitura. Só o alternativo sai — o código canônico é a
 * identidade da peça e não tem rota de remoção.
 *
 * Vai por query e não por corpo: `DELETE` não carrega corpo neste cliente
 * (ver `plataforma/api.ts`), e os dois identificadores (SKU e código) cabem
 * na URL sem o problema de escape que a rota de escrita evita ao aceitar o
 * SKU só no corpo.
 */
export function removerCodigoAlternativo(
  produtoCodigo: string,
  sku: string,
  codigo: string,
): Promise<SaidaCodigosAlternativos> {
  const params = new URLSearchParams({ sku, valor: codigo })
  return api.delete<SaidaCodigosAlternativos>(
    `/produtos/${encodeURIComponent(produtoCodigo)}/codigos-alternativos?${params}`,
  )
}

/** Limites que a tela precisa saber ANTES de tentar o upload — espelham a 0013. */
export const MAX_FOTOS = 6
export const MAX_BYTES_FOTO = 2 * 1024 * 1024
export const TIPOS_DE_FOTO = ['image/jpeg', 'image/png']

/**
 * Recusa o arquivo antes de subir 2 MiB para receber 422.
 *
 * Devolve a razão em pt-BR, ou vazio quando o arquivo serve. É orientação, como
 * o medidor de força de senha: quem decide é o servidor, que valida de novo —
 * inclusive conferindo se o conteúdo bate com o tipo declarado.
 */
export function motivoParaRecusarFoto(arquivo: File): string {
  if (!TIPOS_DE_FOTO.includes(arquivo.type)) {
    return `"${arquivo.name}" não é JPEG nem PNG.`
  }
  if (arquivo.size > MAX_BYTES_FOTO) {
    return `"${arquivo.name}" tem ${formatarBytes(arquivo.size)} e o limite é 2 MiB por foto.`
  }
  return ''
}

/** Formata bytes com aritmética inteira — nada de float para escrever "1,4 MB". */
export function formatarBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
  const decimos = Math.round((bytes * 10) / (1024 * 1024))
  return `${Math.floor(decimos / 10)},${decimos % 10} MB`
}

/**
 * Classifica uma coluna da tira para a cor do badge.
 *
 * Zero é vermelho, no mínimo ou abaixo é âmbar, acima é verde — a regra da
 * badge numérica da spec 01. Os limites vêm do `estoque_minimo` do SKU e não
 * de um número fixo na tela: loja de tênis trabalha mínimo 1 por numeração e
 * loja de camiseta trabalha 4.
 */
export function riscoDoTamanho(posicao: PosicaoGrade): 'zerado' | 'baixo' | 'ok' {
  if (posicao.saldo === 0) return 'zerado'
  if (posicao.saldo <= posicao.estoque_minimo) return 'baixo'
  return 'ok'
}

/**
 * O mesmo para uma célula da matriz da ficha.
 *
 * `ausente` é o quarto estado, e é o que quase todo sistema erra: "esta cor
 * não vem neste tamanho" desenhado como travessão, não como zero. Zero
 * vermelho numa posição que não existe manda comprar o que o fornecedor não
 * fabrica.
 */
export function riscoDaCelula(celula: CelulaFicha): 'ausente' | 'zerado' | 'baixo' | 'ok' {
  if (!celula.existe) return 'ausente'
  if (celula.saldo === 0) return 'zerado'
  if (celula.saldo <= celula.estoque_minimo) return 'baixo'
  return 'ok'
}

/**
 * Calcula a margem em pontos-base para a prévia AO VIVO do formulário.
 *
 * É a mesma conta de `MargemPontos` no Go — `(preço − custo) / preço` sobre o
 * preço de venda, nunca sobre o custo (spec 05) —, e existe aqui só para o
 * campo mostrar a margem enquanto a pessoa digita. O número que VALE é o que o
 * servidor devolve; este é orientação, como o medidor de força de senha.
 *
 * Aritmética inteira: nada de `/ 100` em ponto flutuante no meio do caminho
 * (CONSTITUICAO.md §1).
 */
export function margemPontos(
  precoCentavos: Centavos,
  custoCentavos: Centavos,
): Pontos | null {
  if (precoCentavos <= 0) return null

  const numerador = (precoCentavos - custoCentavos) * 10000
  const negativo = numerador < 0
  const absoluto = negativo ? -numerador : numerador
  const pontos = Math.floor((absoluto + Math.floor(precoCentavos / 2)) / precoCentavos)
  return negativo ? -pontos : pontos
}
